import type { BigWinTuning, FeatureFinishedTuning, FeatureTriggerTuning } from "@slot/game-shell";
import type { BigWinMotionTuning } from "../views/big-win/BigWinView";
import type { PanelMotionTuning } from "../views/PanelMotion";

// The round panels' VALUES — the only file to edit to retune them. The shell's parts (the arm delays, the sounds and
// the music the presenters ask for, the big win's count-up) go whole to its presenters in createPresentation; the
// motion parts are the views'. The title ships no sounds yet: the ids below are the platform's names, silent until
// the title's audio bank registers them.

export const FEATURE_TRIGGER_TUNING: FeatureTriggerTuning = {
  armDelayMs: 800,
  openSoundId: "feature_trigger_panel_open",
  closeSoundId: "feature_trigger_panel_close",
  music: {
    idleId: "music_feature_idle",
    idleFadeInDelayMs: 400,
    idleFadeMs: 1500,
    loopId: "music_feature_hold_and_win",
    loopFadeMs: 1500,
  },
};

export const FEATURE_FINISHED_TUNING: FeatureFinishedTuning = {
  armDelayMs: 800,
  music: {
    idleId: "music_feature_idle",
    finishedIntroId: "music_feature_finished_intro",
    loopId: "music_feature_hold_and_win",
    loopFadeMs: 1500,
    baseId: "music_base",
  },
};

/** The tier breakpoints are the server's (`initialSettings.bigWinTiers`); this is how the panel counts and closes. */
export const BIG_WIN_TUNING: BigWinTuning = {
  autoCloseTimeoutMs: 5000,
  niceAutoCloseMs: 1200,
  instantTierReveal: false,
  niceTierNumberOnly: true,
  countUp: { baseSeconds: 1.8, perDigitSeconds: 0.6, maxSeconds: 8, minMs: 150, easing: "easeIn", easingExponent: 2 },
};

/** The trigger and finished cards: the dim under them, their pop in and shrink out, the hint. */
export const PANEL_MOTION: PanelMotionTuning = {
  dimInSeconds: 0.3,
  dimOutSeconds: 0.25,
  popSeconds: 0.45,
  popFromScale: 0.55,
  exitSeconds: 0.22,
  exitScale: 0.8,
  continueFadeInSeconds: 0.4,
  continueFadeOutSeconds: 0.2,
};

/**
 * The way into the bonus (the artist's potion explosion, the HOLD & WIN card seated under it), on the panels' runner:
 * neither turbo nor the slam shortens it.
 */
export const BONUS_TRANSITION_TUNING = {
  /** Its explosion (the artist's 120 frames at 60 a second), drawn this long, then hidden. */
  seconds: 120 / 60,
  /** The card is seated under it this long after it starts: the artist's frame 50, in the frames 46–62 it covers the screen. */
  swapSeconds: 50 / 60,
  /** Its drawn frames (engine seconds between two, at least). */
  frameSeconds: 1 / 30,
  /** Its texture's share of the screen's pixels: the artist's half (the effect is soft and fills the screen). */
  resolution: 0.5,
} as const;

export const BIG_WIN_MOTION: BigWinMotionTuning = {
  dimInSeconds: 0.3,
  amountPopSeconds: 0.4,
  amountFromScale: 0.4,
  titlePopSeconds: 0.45,
  titleFromScale: 0.3,
  pulseSeconds: 0.6,
  pulseScale: 1.05,
  settleSeconds: 0.3,
  settleScale: 1.15,
  exitSeconds: 0.3,
  continueFadeInSeconds: 0.4,
  continueFadeOutSeconds: 0.2,
};
